import { useEffect, useState } from 'react';
import type { Icon as PhosphorIconComponent } from '@phosphor-icons/react';

// @phosphor-icons/react's namespace export (`import * as PhosphorIcons`) pulls in all
// ~1500 icons (every weight) because dynamic property access (`PhosphorIcons[name]`)
// defeats bundler tree-shaking. A single `import('@phosphor-icons/react')` barrel import
// has the same problem at chunk level: it's one ~1MB chunk regardless of how many
// distinct icon names a given page actually renders — e.g. /signup renders zero
// Phosphor icons but any page that renders even one used to pull in the whole library.
//
// import.meta.glob here targets the package's own per-icon ESM files (every icon ships
// its own `dist/csr/<Name>.es.js` — see its package.json `exports` map for `./*`), so
// Vite code-splits each icon into its own tiny chunk. A page only ever pays network cost
// for the exact icon names it renders.
type PhosphorModule = Record<string, PhosphorIconComponent>;

const iconLoaders = import.meta.glob<PhosphorModule>(
  '/node_modules/@phosphor-icons/react/dist/csr/*.es.js'
);

const loaderByIconName = new Map<string, () => Promise<PhosphorModule>>();
for (const path in iconLoaders) {
  const match = /\/([^/]+)\.es\.js$/.exec(path);
  if (match) loaderByIconName.set(match[1], iconLoaders[path] as () => Promise<PhosphorModule>);
}

const iconCache = new Map<string, PhosphorIconComponent | null>();
const iconPromises = new Map<string, Promise<PhosphorIconComponent | null>>();

/**
 * Loads (and caches) a single icon's component by name, exported so tests can await
 * the exact in-flight promise the hook itself resolves against instead of guessing at
 * a fixed delay for Vite's on-demand transform of the dynamically import()-ed chunk.
 */
export function loadIcon(name: string): Promise<PhosphorIconComponent | null> {
  const pending = iconPromises.get(name);
  if (pending) return pending;

  const loader = loaderByIconName.get(name);
  const promise = (loader ? loader().then((mod) => mod[name] ?? null) : Promise.resolve(null))
    .then((component) => {
      iconCache.set(name, component);
      return component;
    });

  iconPromises.set(name, promise);
  return promise;
}

/**
 * Resolves a Phosphor icon component by its string name (e.g. "PencilSimple"), lazily
 * loading that one icon's own chunk via @phosphor-icons/react's per-icon ESM files
 * instead of the whole library.
 *
 * Returns:
 * - `undefined` while that icon's chunk is still loading (brief, one-time per icon name
 *   per session — see AppIcon.tsx for how callers render this state)
 * - `null` once loaded, if no icon name was given or the name doesn't exist
 * - the resolved component otherwise
 */
export function usePhosphorIcon(iconName: string | null | undefined): PhosphorIconComponent | null | undefined {
  const [, forceRerender] = useState(0);

  useEffect(() => {
    if (!iconName || iconCache.has(iconName)) return;
    let cancelled = false;
    loadIcon(iconName).then(() => {
      if (!cancelled) forceRerender((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [iconName]);

  if (!iconName) return null;
  if (!iconCache.has(iconName)) return undefined;
  return iconCache.get(iconName) ?? null;
}

export default usePhosphorIcon;
